import { ArrowLeftRight } from "lucide-react";

function formatHoursCell(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0.0";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numeric);
}

export function LaborUnmappedOpCodesTable({
  rows = [],
  operationDescriptionByCode,
  loading = false,
  assignDisabled = false,
  onAllocateOpCode,
}) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Unmapped Op Codes</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Op codes not assigned to any invoice line — assign all hours to a labor line
      </p>

      {loading ? (
        <div className="mt-4 space-y-3">
          <div className="h-10 w-full animate-pulse rounded bg-gray-200/80 dark:bg-gray-700/80" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200/80 dark:bg-gray-700/80" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200/80 dark:bg-gray-700/80" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No unmapped op codes for this invoice.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[40rem]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-sm font-semibold text-[#1a3268] dark:text-blue-300">
                  Op Code
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-[#1a3268] dark:text-blue-300">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-[#1a3268] dark:text-blue-300">
                  Hours Available
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-[#1a3268] dark:text-blue-300">
                  Hours Unassigned
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[#1a3268] dark:text-blue-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {rows.map((row) => {
                const opCode = String(row.opCode ?? "").trim();
                const description = operationDescriptionByCode?.get(opCode) ?? opCode;
                const hoursNotAllocated = Number(row.hoursNotAllocated);
                const isFullyAllocated =
                  Number.isFinite(hoursNotAllocated) && Math.abs(hoursNotAllocated) < 0.05;
                return (
                  <tr key={opCode} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {opCode}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {description}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 text-right tabular-nums whitespace-nowrap">
                      {formatHoursCell(row.hoursAvailable)}
                    </td>
                    <td
                      className={`px-4 py-4 text-sm text-right tabular-nums whitespace-nowrap font-medium ${
                        isFullyAllocated
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {formatHoursCell(row.hoursNotAllocated)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => onAllocateOpCode?.(opCode)}
                        disabled={assignDisabled}
                        className="app-btn-plain inline-flex items-center justify-center p-1.5 text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-blue-400 dark:hover:bg-blue-950/30 rounded transition-colors"
                        title="Assign to invoice line"
                        aria-label={`Assign ${opCode} to invoice line`}
                      >
                        <ArrowLeftRight className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
